// =========================================================
// Vojjin DOM Utilities
// =========================================================

;(function (global) {
    // Create namespace
    const G = global.gemini || (global.gemini = {});
    G.dom = {};

    function assertElement(selector, root = document) {
        if (!G.dom.exists(selector, root)) {
            console.warn(`No elements found for selector: "${selector}"`);
            return false;
        }
        return true;
    }

    function $$(selector, root = document) {
        if (typeof selector === "string")
            return Array.from(root.querySelectorAll(selector));
        if (selector instanceof Element)
            return [selector];
        if (selector instanceof NodeList || Array.isArray(selector))
            return Array.from(selector);
        return [];
    }

    G.dom.exists = function (selector, root = document) {
        return $$(selector, root).length > 0;
    };

    G.dom.get = function (selector, index = null, root = document) {
        if (!(typeof index === 'string' || typeof index === 'number') && index !== undefined && index !== null) {
            root = index;
            index = undefined;
        }
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        if (!Array.isArray(els) || els.length === 0) return null;
        if (index === undefined || index === null) index = 0;

        if (index === "all") return els;
        if (typeof index === "number") return els[index] ?? null;
        return null;
    };

    G.dom.each = function (selector, callback, root = document) {
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        els.forEach((el, i) => callback(el, i));
    };

    G.dom.hasClass = function (selector, className, root = document) {
        if (!assertElement(selector, root)) return false;
        const els = $$(selector, root);
        return els.some(el => el.classList.contains(className));
    };

    G.dom.addClass = function (selector, className, root = document) {
        if (!assertElement(selector, root)) return;
        const els = $$(selector, root);
        els.forEach(el => el.classList.add(className));
    };

    G.dom.removeClass = function (selector, classNames, root = document) {
        if (!assertElement(selector, root)) return;
        const els = $$(selector, root);
        els.forEach(el => el.classList.remove(classNames));
    };

    G.dom.toggleClass = function (selector, className, root = document) {
        if (!assertElement(selector, root)) return;
        const els = $$(selector, root);
        els.forEach(el => el.classList.toggle(className));
    };

    G.dom.css = function (selector, prop, value, root = document) {
        if (!(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            root = value;
            value = undefined;
        }

        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        // READ MODE: css(el, "color")
        if (typeof prop === "string" && value === undefined) {
            const el = els[0];
            if (!el) return null;
            return getComputedStyle(el).getPropertyValue(prop);
        }

        if (typeof prop === "string" && value !== undefined) {
            els.forEach(el => (el.style[prop] = value));
            return;
        }

        if (typeof prop === "object") {
            els.forEach(el => {
                for (let [key, val] of Object.entries(prop)) {
                    el.style[key] = val;
                }
            });
        }
    };

    G.dom.text = function (selector, value, root = document) {
        if (!(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            root = value;
            value = undefined;
        }
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        if (value === undefined) return els[0]?.textContent ?? null;
        els.forEach(el => (el.textContent = value));
    };

    G.dom.html = function (selector, value, root = document) {
        if (!(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            root = value;
            value = undefined;
        }
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        if (value === undefined) return els[0]?.innerHTML ?? null;
        els.forEach(el => (el.innerHTML = value));
    };

    G.dom.attr = function (selector, attributeName, value, root = document) {
        if (!(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            root = value;
            value = undefined;
        }
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        if (value === undefined) return els[0]?.getAttribute(attributeName) ?? null;
        els.forEach(el => el.setAttribute(name, value));
    };

    G.dom.data = function (selector, key, value, root = document) {
        if (!(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            root = value;
            value = undefined;
        }
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        if (value === undefined) return els[0]?.dataset?.[key] ?? null;
        els.forEach(el => (el.dataset[key] = value));
    };

    G.dom.val = function (selector, value, root = document) {
        if (!(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            root = value;
            value = undefined;
        }
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        if (value === undefined) return els[0]?.value ?? null;
        els.forEach(el => (el.value = value));
    };

    G.dom.on = function (selector, eventType, handler, root = document) {
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        els.forEach(el => el.addEventListener(eventType, handler));
    };

    G.dom.remove = (selector, root = document) => {
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        els.forEach(el => el.remove());
    };

    G.dom.closest = (selector, targetSelector, root = document) => {
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        return els[0].closest(targetSelector);
    }

    G.dom.parent = (selector, root = document) => {
        if (!assertElement(selector, root)) return null;
        const els = $$(selector, root);
        return els[0].parentNode;
    }


    if (typeof module !== "undefined" && module.exports) {
        module.exports = G.dom;
    } else {
        global.gemini = G;
        global.gemini.dom = G.dom;

    }
})(typeof globalThis !== "undefined" ? globalThis : this);
